import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  Paper,
  Dialog,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { parseFee, parseIncome } from './helpers';

/* ── i18n labels ── */
const L = {
  es: {
    title: 'Encuentra tu tarjeta ideal',
    subtitle: 'Responde 4 preguntas rápidas y te recomendamos las mejores opciones.',
    stepOf: (a, t) => `Paso ${a} de ${t}`,
    takesLess: 'Toma menos de 1 minuto',
    seeResults: 'Ver mis resultados',
    back: 'Atrás',
    skip: 'Saltar al comparador completo',
    q1: '¿Cuánto tiempo llevas en Canadá?',
    q1_new: 'Menos de 6 meses', q1_new_sub: 'Recién llegado, sin historial todavía',
    q1_6mo: '6 meses a 2 años', q1_6mo_sub: 'Ya empezando a construir crédito',
    q1_2yr: '2 a 5 años', q1_2yr_sub: 'Buscando mejores beneficios',
    q1_5yr: 'Más de 5 años', q1_5yr_sub: 'Opciones más amplias y premium',
    q2: '¿Tienes historial crediticio en Canadá?',
    q2_none: 'No, soy nuevo', q2_none_sub: 'Empiezo desde cero en Canadá',
    q2_building: 'Estoy construyéndolo', q2_building_sub: 'Tengo tarjeta desde hace poco',
    q2_established: 'Sí, ya tengo historial', q2_established_sub: 'Score activo y en buen rango',
    q3: '¿Qué es lo más importante para ti?',
    q3_nofee: 'Sin cuota anual', q3_nofee_sub: 'Que no me cueste tenerla',
    q3_credit: 'Construir crédito', q3_credit_sub: 'Empezar a tener score',
    q3_cashback: 'Cash back', q3_cashback_sub: 'Recuperar dinero en compras',
    q3_travel: 'Viajes y millas', q3_travel_sub: 'Acumular puntos para volar',
    q4: '¿Cuál es tu ingreso anual aproximado?',
    q4_under30: 'Menos de $30,000', q4_under30_sub: 'Tiempo parcial o recién empezando',
    q4_30_60: '$30,000 – $60,000', q4_30_60_sub: 'Ingreso medio en Canadá',
    q4_60_100: '$60,000 – $100,000', q4_60_100_sub: 'Más opciones disponibles',
    q4_100plus: 'Más de $100,000', q4_100plus_sub: 'Acceso a tarjetas premium',
  },
  en: {
    title: 'Find your ideal card',
    subtitle: 'Answer 4 quick questions and we\'ll recommend the best options.',
    stepOf: (a, t) => `Step ${a} of ${t}`,
    takesLess: 'Takes less than 1 minute',
    seeResults: 'See my results',
    back: 'Back',
    skip: 'Skip to full comparator',
    q1: 'How long have you been in Canada?',
    q1_new: 'Less than 6 months', q1_new_sub: 'Just arrived, no history yet',
    q1_6mo: '6 months to 2 years', q1_6mo_sub: 'Already starting to build credit',
    q1_2yr: '2 to 5 years', q1_2yr_sub: 'Looking for better benefits',
    q1_5yr: 'More than 5 years', q1_5yr_sub: 'Wider and premium options',
    q2: 'Do you have credit history in Canada?',
    q2_none: 'No, I\'m new', q2_none_sub: 'Starting from scratch in Canada',
    q2_building: 'I\'m building it', q2_building_sub: 'Got a card recently',
    q2_established: 'Yes, I have history', q2_established_sub: 'Active score in good range',
    q3: 'What\'s most important to you?',
    q3_nofee: 'No annual fee', q3_nofee_sub: 'Keep it free',
    q3_credit: 'Build credit', q3_credit_sub: 'Start building a score',
    q3_cashback: 'Cash back', q3_cashback_sub: 'Get money back on purchases',
    q3_travel: 'Travel & miles', q3_travel_sub: 'Earn points to fly',
    q4: 'What\'s your approximate annual income?',
    q4_under30: 'Under $30,000', q4_under30_sub: 'Part-time or just starting',
    q4_30_60: '$30,000 – $60,000', q4_30_60_sub: 'Average income in Canada',
    q4_60_100: '$60,000 – $100,000', q4_60_100_sub: 'More options available',
    q4_100plus: 'Over $100,000', q4_100plus_sub: 'Access to premium cards',
  },
  fr: {
    title: 'Trouvez votre carte idéale',
    subtitle: 'Répondez à 4 questions rapides et nous vous recommanderons les meilleures options.',
    stepOf: (a, t) => `Étape ${a} de ${t}`,
    takesLess: 'Prend moins d\'une minute',
    seeResults: 'Voir mes résultats',
    back: 'Retour',
    skip: 'Passer au comparateur complet',
    q1: 'Depuis combien de temps êtes-vous au Canada?',
    q1_new: 'Moins de 6 mois', q1_new_sub: 'Nouvel arrivant, pas encore d\'historique',
    q1_6mo: '6 mois à 2 ans', q1_6mo_sub: 'Déjà en train de bâtir son crédit',
    q1_2yr: '2 à 5 ans', q1_2yr_sub: 'À la recherche de meilleurs avantages',
    q1_5yr: 'Plus de 5 ans', q1_5yr_sub: 'Options plus larges et premium',
    q2: 'Avez-vous un historique de crédit au Canada?',
    q2_none: 'Non, je suis nouveau', q2_none_sub: 'Je commence à zéro au Canada',
    q2_building: 'Je le construis', q2_building_sub: 'J\'ai une carte depuis peu',
    q2_established: 'Oui, j\'ai un historique', q2_established_sub: 'Score actif et en bon rang',
    q3: 'Qu\'est-ce qui est le plus important pour vous?',
    q3_nofee: 'Sans frais annuels', q3_nofee_sub: 'Que ça ne coûte rien',
    q3_credit: 'Bâtir mon crédit', q3_credit_sub: 'Commencer à avoir un score',
    q3_cashback: 'Remises en argent', q3_cashback_sub: 'Récupérer de l\'argent sur les achats',
    q3_travel: 'Voyages et milles', q3_travel_sub: 'Accumuler des points pour voyager',
    q4: 'Quel est votre revenu annuel approximatif?',
    q4_under30: 'Moins de 30 000 $', q4_under30_sub: 'Temps partiel ou début de carrière',
    q4_30_60: '30 000 $ – 60 000 $', q4_30_60_sub: 'Revenu moyen au Canada',
    q4_60_100: '60 000 $ – 100 000 $', q4_60_100_sub: 'Plus d\'options disponibles',
    q4_100plus: 'Plus de 100 000 $', q4_100plus_sub: 'Accès aux cartes premium',
  },
};

const getQuestions = (l) => [
  {
    id: 'time_in_canada',
    question: l.q1,
    options: [
      { value: 'new', label: l.q1_new, sub: l.q1_new_sub, img: '/images/wizard/recien-llegado.webp', alt: l.q1_new },
      { value: '6mo_2yr', label: l.q1_6mo, sub: l.q1_6mo_sub, img: '/images/wizard/seis-meses-dos-anos.webp', alt: l.q1_6mo },
      { value: '2yr_5yr', label: l.q1_2yr, sub: l.q1_2yr_sub, img: '/images/wizard/dos-cinco-anos.webp', alt: l.q1_2yr },
      { value: '5yr_plus', label: l.q1_5yr, sub: l.q1_5yr_sub, img: '/images/wizard/mas-cinco-anos.webp', alt: l.q1_5yr },
    ],
  },
  {
    id: 'credit_history',
    question: l.q2,
    options: [
      { value: 'none', label: l.q2_none, sub: l.q2_none_sub, img: '/images/wizard/nuevo-sin-historial.webp', alt: l.q2_none },
      { value: 'building', label: l.q2_building, sub: l.q2_building_sub, img: '/images/wizard/construyendo-credito.webp', alt: l.q2_building },
      { value: 'established', label: l.q2_established, sub: l.q2_established_sub, img: '/images/wizard/historial-establecido.webp', alt: l.q2_established },
    ],
  },
  {
    id: 'priority',
    question: l.q3,
    options: [
      { value: 'no_fee', label: l.q3_nofee, sub: l.q3_nofee_sub, img: '/images/wizard/sin-cuota-anual.webp', alt: l.q3_nofee },
      { value: 'build_credit', label: l.q3_credit, sub: l.q3_credit_sub, img: '/images/wizard/construir-credito.webp', alt: l.q3_credit },
      { value: 'cashback', label: l.q3_cashback, sub: l.q3_cashback_sub, img: '/images/wizard/cashback-reembolso.webp', alt: l.q3_cashback },
      { value: 'travel', label: l.q3_travel, sub: l.q3_travel_sub, img: '/images/wizard/viajes-millas.webp', alt: l.q3_travel },
    ],
  },
  {
    id: 'income_range',
    question: l.q4,
    options: [
      { value: 'under_30k', label: l.q4_under30, sub: l.q4_under30_sub, img: '/images/wizard/ingreso-bajo.webp', alt: l.q4_under30 },
      { value: '30k_60k', label: l.q4_30_60, sub: l.q4_30_60_sub, img: '/images/wizard/ingreso-medio.webp', alt: l.q4_30_60 },
      { value: '60k_100k', label: l.q4_60_100, sub: l.q4_60_100_sub, img: '/images/wizard/ingreso-alto.webp', alt: l.q4_60_100 },
      { value: '100k_plus', label: l.q4_100plus, sub: l.q4_100plus_sub, img: '/images/wizard/ingreso-premium.webp', alt: l.q4_100plus },
    ],
  },
];

const INCOME_MIDPOINTS = {
  under_30k: 20000,
  '30k_60k': 45000,
  '60k_100k': 80000,
  '100k_plus': 130000,
};

export const scoreCard = (card, answers) => {
  let score = 0;
  const { time_in_canada, credit_history, priority, income_range } = answers;

  // Newcomer-friendly bonus
  if ((time_in_canada === 'new' || time_in_canada === '6mo_2yr') && card.newcomer_friendly) {
    score += 30;
  }
  if (credit_history === 'none' && card.newcomer_friendly) {
    score += 25;
  }
  if (credit_history === 'building' && card.newcomer_friendly) {
    score += 15;
  }

  // Priority matching
  const fee = parseFee(card.annual_fee);
  const rewards = (card.rewards || '').toLowerCase();

  switch (priority) {
    case 'no_fee':
      if (fee === 0) score += 35;
      else if (fee < 30) score += 10;
      break;
    case 'build_credit':
      if (card.newcomer_friendly) score += 25;
      if (fee === 0) score += 15;
      break;
    case 'cashback':
      if (rewards.includes('cash') || rewards.includes('reembolso') || rewards.includes('money back')) score += 35;
      if (rewards.includes('%')) score += 10;
      break;
    case 'travel':
      if (rewards.includes('travel') || rewards.includes('viaj') || rewards.includes('miles') || rewards.includes('points') || rewards.includes('aeroplan')) score += 30;
      if (card.insurance && card.insurance.travel_medical && card.insurance.travel_medical !== 'Not available') score += 15;
      break;
    default:
      break;
  }

  // Bonus for no-fee cards when newcomer
  if (time_in_canada === 'new' && fee === 0) {
    score += 10;
  }

  // Income match bonus
  if (income_range) {
    const midpoint = INCOME_MIDPOINTS[income_range] || 45000;
    const requiredIncome = parseIncome(card.min_income_personal);
    if (requiredIncome === 0 || requiredIncome <= midpoint) {
      score += 15;
    }
  }

  return score;
};

/* ── Progress bar ─────────────────────────────── */
const ProgressBar = ({ total, active, l }) => {
  const pct = ((active + 1) / total) * 100;
  return (
    <Box sx={{ maxWidth: 320, mx: 'auto', mb: 0.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1E3A5F' }}>
          {l.stepOf(active + 1, total)}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
          {l.takesLess}
        </Typography>
      </Box>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#EEF0F3', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: 2,
            bgcolor: '#1E3A5F',
            transition: 'width 0.4s ease',
          }}
        />
      </Box>
    </Box>
  );
};

/* ── Wizard content ───────────────────────────── */
const WizardContent = ({ onComplete, onSkip, l }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const QUESTIONS = getQuestions(l);
  const currentQuestion = QUESTIONS[activeStep];
  const isLastStep = activeStep === QUESTIONS.length - 1;

  const handleSelect = (value) => {
    const updated = { ...answers, [currentQuestion.id]: value };
    setAnswers(updated);

    if (!isLastStep) {
      setTimeout(() => setActiveStep((s) => s + 1), 180);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((s) => s - 1);
  };

  const handleSubmit = () => {
    onComplete(answers);
  };

  const allAnswered = QUESTIONS.every((q) => answers[q.id]);

  return (
    <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
      {/* Header */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: '#1A1A1A',
          mb: 0.25,
          textAlign: 'center',
          fontSize: { xs: '1.3rem', md: '1.6rem' },
        }}
      >
        {l.title}
      </Typography>
      <Typography
        sx={{ color: '#5F6B7A', mb: 2.5, textAlign: 'center', fontSize: '14px' }}
      >
        {l.subtitle}
      </Typography>

      {/* Progress bar */}
      <ProgressBar total={QUESTIONS.length} active={activeStep} l={l} />

      {/* Question */}
        <Box key={activeStep} sx={{ animation: 'wizFadeIn 0.25s ease', '@keyframes wizFadeIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'translateY(0)' } } }}>
          <Typography
            sx={{
              fontWeight: 600,
              mt: 2.5,
              mb: 2,
              textAlign: 'center',
              color: '#1A1A1A',
              fontSize: { xs: '1.05rem', md: '1.2rem' },
            }}
          >
            {currentQuestion.question}
          </Typography>

          {/* Options */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                md: `repeat(${Math.min(currentQuestion.options.length, 4)}, 1fr)`,
              },
              gap: { xs: 1.5, md: 2 },
              mb: 2.5,
            }}
          >
            {currentQuestion.options.map((opt) => {
              const isSelected = answers[currentQuestion.id] === opt.value;
              return (
                <ButtonBase
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  sx={{
                    py: 2,
                    px: 1.5,
                    minHeight: { xs: 110, md: 130 },
                    borderRadius: '14px',
                    border: '2px solid',
                    borderColor: isSelected ? '#1E3A5F' : '#EEF0F3',
                    bgcolor: isSelected ? 'rgba(30,58,95,0.05)' : '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                    transition: 'all 180ms ease',
                    position: 'relative',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: isSelected ? '#1E3A5F' : '#B0B8C4',
                      bgcolor: isSelected ? 'rgba(30,58,95,0.05)' : 'rgba(30,58,95,0.02)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  {isSelected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: '#1E3A5F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />
                    </Box>
                  )}
                  {opt.img && (
                    <Box
                      component="img"
                      src={opt.img}
                      alt={opt.alt || opt.label}
                      loading="lazy"
                      sx={{ width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 }, objectFit: 'contain' }}
                    />
                  )}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        fontWeight: 600,
                        color: '#1A1A1A',
                        lineHeight: 1.3,
                      }}
                    >
                      {opt.label}
                    </Typography>
                    {opt.sub && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: '#9CA3AF',
                          mt: 0.25,
                          lineHeight: 1.3,
                        }}
                      >
                        {opt.sub}
                      </Typography>
                    )}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>

          {/* Submit button on last step */}
          {isLastStep && answers[currentQuestion.id] && (
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={!allAnswered}
              disableElevation
              sx={{
                bgcolor: '#1E3A5F',
                color: '#fff',
                fontWeight: 700,
                py: 1.5,
                borderRadius: '12px',
                fontSize: '1rem',
                mb: 1.5,
                textTransform: 'none',
                '&:hover': { bgcolor: '#1A3152' },
              }}
            >
              {l.seeResults}
            </Button>
          )}

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            {activeStep > 0 && (
              <Button
                startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                onClick={handleBack}
                sx={{ color: '#5F6B7A', textTransform: 'none', fontSize: '13px', fontWeight: 500 }}
              >
                {l.back}
              </Button>
            )}
            <Button
              onClick={onSkip}
              sx={{ color: '#9CA3AF', textTransform: 'none', fontSize: '12px', fontWeight: 500, '&:hover': { color: '#5F6B7A', bgcolor: 'transparent' } }}
            >
              {l.skip}
            </Button>
          </Box>
        </Box>
    </Box>
  );
};

/* ── SmartWizard - main export ────────────────── */
const SmartWizard = ({ onComplete, onSkip }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const l = L[lang] || L.es;

  if (isMobile) {
    return (
      <Dialog open fullScreen>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: '100%',
            px: 1,
          }}
        >
          <WizardContent onComplete={onComplete} onSkip={onSkip} l={l} />
        </Box>
      </Dialog>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 4,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <WizardContent onComplete={onComplete} onSkip={onSkip} l={l} />
    </Paper>
  );
};

export default SmartWizard;
